"use client";

import type { ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import ButtonBase from "@mui/material/ButtonBase";
import Header from "@/app/components/header";
import Footer from "@/app/components/footer";
import { AboutLayoutHelmet } from "@/app/(public)/_seo/PublicLayoutMetadata";
import {
  PUBLIC_PAGE_CONTAINER_MAX_WIDTH,
  publicPageContainerSx,
} from "@/app/_utils/publicLayout";
import ScrollReveal from "@/app/components/ScrollReveal";

const pageFont =
  '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const palette = {
  bg: "#000000",
  /** Timeline text panels — same family as the original two-card strip */
  bubbleSurface: "#1a1a1e",
  /** Softer than pure black for logo / contain wells — reads less like empty void */
  imageWellContain: "#15151a",
  border: "rgba(255, 255, 255, 0.08)",
  muted: "rgba(255, 255, 255, 0.62)",
  accent: "#c9a84c",
  /** Vertical spine + horizontal connectors — hairline white on black */
  spine: "rgba(255, 255, 255, 0.88)",
};

const milestoneExternalLinkSx = {
  color: "inherit",
  fontWeight: 600,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  "&:hover": { color: palette.accent },
} as const;

const AboutCard = ({
  imageSrc,
  title,
  subtitle,
  description,
}: {
  imageSrc: string;
  title: string;
  subtitle?: string;
  description: string;
}) => (
  <Box
    sx={{
      borderRadius: "12px",
      overflow: "hidden",
      backgroundColor: "rgba(30, 30, 30, 0.95)",
      fontFamily: pageFont,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    }}
  >
    <Box
      component="img"
      src={imageSrc}
      alt=""
      sx={{
        width: "100%",
        height: { xs: 240, sm: 260, md: 280 },
        objectFit: "cover",
        display: "block",
        flexShrink: 0,
      }}
    />
    <Box
      sx={{
        p: 2.5,
        backgroundColor: "rgba(40, 40, 45, 0.98)",
        borderRadius: "0 0 12px 12px",
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: pageFont,
          fontWeight: 700,
          fontSize: { xs: "1.35rem", sm: "1.5rem" },
          color: "#fff",
          mb: 0.5,
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            fontFamily: pageFont,
            fontSize: "0.7rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.8)",
            mb: 1.5,
          }}
        >
          {subtitle}
        </Typography>
      )}
      <Typography
        sx={{
          fontFamily: pageFont,
          fontSize: "0.9375rem",
          lineHeight: 1.7,
          color: "rgba(255,255,255,0.9)",
          wordBreak: "break-word",
          flex: 1,
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

type Milestone = {
  title: string;
  subtitle?: string;
  body: ReactNode;
  image: string;
  imageAlt: string;
  /** Use `contain` for logos / artwork that must stay fully visible */
  imageFit?: "cover" | "contain";
  /** Max width/height % inside the well when using contain (default below) */
  imageContainPercent?: number;
};

type YearBlock = {
  year: string;
  theme?: string;
  milestones: Milestone[];
};

const TIMELINE: YearBlock[] = [
  {
    year: "2021",
    theme: "Humble Beginnings",
    milestones: [
      {
        title: "Humble Beginnings",
        subtitle: "The First Show — April 6, 2021",
        body:
          "We hosted our first-ever show in our friend Terrence Walcott's gym. Terrence helped David recover from a torn ACL, and he opened his gym in March of 2020. Terrific timing. Forty people squeezed into the space. One speaker. No spotlight. The equipment we fully intended to return to B&H within 30 days. Mark Normand headlined and brought Ian Lara with him. David hosted… and bombed immediately. It was chaotic, underproduced, and somehow enough to make us think: this whole pop-up comedy idea could actually be something.",
        image:
          "https://storage.googleapis.com/cuo-website/UO+Outline+Logo.png",
        imageAlt: "Underground Overground outline logo",
        imageFit: "contain",
        imageContainPercent: 52,
      },
      {
        title: "First Press",
        subtitle: "New York Post feature",
        body:
          "Our show at La La Laundry caught the attention of the New York Post. That article changed everything. The following for our somewhat secret comedy shows started to grow after the article, allowing us to bring comedy to even more unique spaces. Suddenly, people outside our immediate circle knew who we were. If you were looking for pop-up comedy in NYC, we might show up on your feed. Despite thinking we were becoming big time, we still made the effort to make every little aspect of the shows fun, even if it involved manually spiking 150 Capri Suns.",
        image:
          "https://storage.googleapis.com/cuo-website/Most+popular+comedy+clubs.jpg",
        imageAlt: "Comedy show crowd and venue",
      },
      {
        title: "First Iconic Venue",
        subtitle: "Economy Candy",
        body:
          "We landed our first true New York institution, and almost blew it. The headliner canceled three hours before the show. The owners were asking for insurance documents that we did not have (now we always do). We barely had a network to replace the comics, but comedian Reg Thomas helped us, and we hoped for the best. Someone from Time Out New York was in the crowd that night, and the next week they wrote an article naming us the #2 thing to do in NYC. Economy Candy gave us the confidence we needed to start chasing bigger venues, and we still go back every year to honor the tradition.",
        image:
          "https://storage.googleapis.com/cuo-website/secret+nyc+comedy+clubs.jpg",
        imageAlt: "Economy Candy comedy show",
      },
    ],
  },
  {
    year: "2022",
    theme: "Betting on Ourselves",
    milestones: [
      {
        title: "The Logo Is Born",
        subtitle: "Daniel's Leather",
        body:
          "The brand logo wasn't planned. As you can see, this is a common trend for us. At a show at Daniel's Leather, a photo of David and Ethan trying on jackets looked better than anything we could design, so it became the logo of the Underground Overground brand. The red jacket David wore? Bought by CeeLo Green the next day.",
        image:
          "https://storage.googleapis.com/cuo-website/underground+comedy.jpg",
        imageAlt: "Comedy show at Daniel's Leather",
      },
      {
        title: "Katz's Deli",
        subtitle: "The inflection point — November 2022",
        body:
          "Katz's Delicatessen changed everything. We promised to pay them up front before selling a single ticket. It was the first time we truly bet on ourselves financially. It sold out. We shifted from being a couple of kids putting together pop-up comedy shows at your local laundromat, to a real brand activating a huge space with big-time talent. Everyone left with a custom mustard bottle, and that became part of the experience: every show should leave you with something you didn't expect. We got drawn up as cartoon characters in the New Yorker, and during the interview, one of our favorite quotes was from Katz's employee David Manheim: “These kids are f*cking idiots, but they're really passionate.” We've been working on the idiot part, but the passion hasn't changed a bit.",
        image:
          "https://storage.googleapis.com/cuo-website/Katz%27s+Deli.jpg",
        imageAlt: "Comedy show at Katz's Delicatessen",
      },
      {
        title: "Marcello Madness",
        body:
          "We love our comedians all equally, and we would never pick a favorite. This being said, we enjoy looking back and laughing about a particular moment with Marcello Hernández. Marcello had just performed an incredible set for us at Down Dog yoga and rushed off the stage after his last joke. Before he got out the door, he told us he was headed off to a big audition. Here's a hint: the name of the show starts with 'Saturday.'",
        image:
          "https://storage.googleapis.com/cuo-website/New+York+Comedy+Club.jpg",
        imageAlt: "New York comedy venue",
      },
    ],
  },
  {
    year: "2023",
    theme: "Growth & Reality Checks",
    milestones: [
      {
        title: "100 Shows In",
        subtitle: "Paragon Sports",
        body:
          "We celebrated our 100th show at the Paragon Sports Store, something we never imagined when we started. The 100th felt like a milestone. The 101st had one of the worst crowds we've ever seen. That swing taught us everything: momentum doesn't guarantee consistency. You still have to earn it every night. Paragon Sports was one of the toughest venues to convince, as the owner was very skeptical of two 24-year-olds who said “Don't worry, we have everything under control.”",
        image:
          "https://storage.googleapis.com/cuo-website/Comedy+at+Paragon+Sports.jpg",
        imageAlt: "Comedy at Paragon Sports",
      },
      {
        title: "Scaling Up",
        subtitle: "Edge at Hudson Yards",
        body:
          "Hosting a comedy show at The Edge was the first time we saw what pop-up comedy at scale could look like. David and Ethan had to invite one of the event coordinators from Hudson Yards to our show at Paragon to convince him that we were a legit organization. Bigger crowd, bigger expectations, and proof that this wasn't just a niche idea and that there was real demand for comedy in places people had never seen it before. The show at the Edge also gave us marketability outside of New York, allowing us to intrigue newcomers to NYC with a pop-up comedy show that plugged them directly into the DNA of the city.",
        image:
          "https://storage.googleapis.com/cuo-website/new+york+comedy+clubs.jpg",
        imageAlt: "NYC comedy at scale",
      },
      {
        title: "Afterparty Chaos",
        subtitle: "Down Dog Yoga",
        body:
          "We experimented with adding afterparties to our shows starting in 2023. The first venue where we tried it, Down Dog Yoga, had a beautiful outdoor patio where both comedians and attendees could hang out. The NYPD tried to shut us down. Neighbors sprayed us with hoses from their windows. It somehow made the night better. Our relationship with Down Dog is our most storied and unique partnership. One of the co-founders DM'd our IG a picture of a townhouse on Zillow and said he'd love to do an event at his crib. David scoped it out, and there was a gentleman passed out on the steps. After stepping over this stranger, they walked into what you would call a Silicon Valley frat house. With no insurance, we moved hundreds of thousands of dollars' worth of tech equipment to set up for a show.",
        image:
          "https://storage.googleapis.com/cuo-website/things+to+do+nyc+tonight.jpg",
        imageAlt: "Afterparty at Down Dog Yoga",
      },
    ],
  },
  {
    year: "2024",
    theme: "Pressure & Expansion",
    milestones: [
      {
        title: "Laughing at Peter Luger Steak House",
        body:
          "Our show at Peter Luger's marked a full circle moment—from laundromats to one of the most iconic restaurants in New York. This took over 12 months and about 100 cold emails until one day, the younger sister of one of the owners attended one of our shows at a boxing gym in Chelsea. She had a great time and asked if we ever thought about doing an event at Luger's. We showed her all the emails we had sent, and within 48 hours we had a call with the team and picked a date. People loved the show, but they probably enjoyed the burgers more. The burgers at comedy clubs in NY aren't exactly the same as they are at Luger's.",
        image:
          "https://storage.googleapis.com/cuo-website/comedy+in+nyc.jpg",
        imageAlt: "Comedy at Peter Luger",
      },
      {
        title: "Rolling With It",
        body:
          "We had our first real cancellation. A residential building shut us down the night of our 3-year anniversary. Only 20 of the 80 guests made it upstairs. So we ran the show anyway. Our intern hosted, and we played some beer pong.",
        image:
          "https://storage.googleapis.com/cuo-website/cool+shit+in+nyc.jpg",
        imageAlt: "NYC comedy night",
      },
      {
        title: "Double Whammy",
        subtitle: "Two shows, one night",
        body:
          "For the first time, we ran two shows simultaneously: one at Paragon Sports and one at an Allbirds store. It was chaotic, but it forced us to grow our operation. The shows were approximately five minutes from each other, and the host for the Allbirds store show cancelled. We legitimately had JC Mendoza run back and forth between shows to host both events. MVP performance. The show must go on.",
        image:
          "https://storage.googleapis.com/cuo-website/new+york+comedy+.jpg",
        imageAlt: "Two simultaneous comedy shows",
      },
    ],
  },
  {
    year: "2025",
    theme: "Opening New Doors",
    milestones: [
      {
        title: "Signing with WME",
        body:
          "We signed with WME. For a company that started in a gym with returnable speakers, this felt surreal, a moment of true validation. In June of 2025, Comedy UO also hired its first employee: Jake Delforte. He interned in the fall and had a natural obsession for comedy and show business that you can't teach.",
        image:
          "https://storage.googleapis.com/cuo-website/WME+ComedyUO.jpg",
        imageAlt: "ComedyUO and WME",
      },
      {
        title: "Time for Tapings",
        body: (
          <>
            After working with 200+ comedians, we started producing 10-minute taped sets. We finally
            began building a digital platform to showcase the talent we believe in. If you
            haven&apos;t visited the Underground Overground{" "}
            <Link
              href="https://www.youtube.com/@ComedyUO"
              target="_blank"
              rel="noopener noreferrer"
              sx={milestoneExternalLinkSx}
            >
              YouTube
            </Link>{" "}
            or our{" "}
            <Link
              href="https://www.instagram.com/comedy.uo/"
              target="_blank"
              rel="noopener noreferrer"
              sx={milestoneExternalLinkSx}
            >
              Comedy.uo
            </Link>{" "}
            Instagram page, you&apos;re late to the party. (Make sure to like and subscribe.)
          </>
        ),
        image:
          "https://storage.googleapis.com/cuo-website/comedy+nyc.jpg",
        imageAlt: "Comedy tapings",
      },
      {
        title: "Something Magical",
        body:
          "We expanded into pop-up magic shows. Different art form, same philosophy: intimate, surprising, and unforgettable. The response was immediate, and we're just getting started.",
        image:
          "https://storage.googleapis.com/cuo-website/magic+in+nyc.jpg",
        imageAlt: "Magic show in NYC",
      },
    ],
  },
  {
    year: "2026",
    theme: "Making History",
    milestones: [
      {
        title: "Empire State of Mind",
        subtitle: "Empire State Building show",
        body:
          "At the beginning of 2026, we produced the first-ever comedy show at the Empire State Building. That's the sentence. For us, it was proof that the idea of “comedy anywhere” doesn't have a ceiling. Also at the beginning of the year, we brought on our second full-time employee, Mike Z. The website you are reading this on is because of him. He's the only one on the team familiar with this new concept called “AI.”",
        image:
          "https://storage.googleapis.com/cuo-website/empire+state+comedy+show+tonight.jpg",
        imageAlt: "Comedy at the Empire State Building",
      },
      {
        title: "Looking Ahead",
        body: (
          <>
            We still don&apos;t fully know what this becomes. But we know we&apos;re not done pushing
            where comedy can live. More venues. More risks. More moments that probably shouldn&apos;t
            work, but somehow do. If you want to follow along behind the scenes, David shares the
            day-to-day on{" "}
            <Link
              href="https://substack.com/@davidcomedyuo?r=26gbm6&utm_medium=ios&utm_source=stories&shareImageVariant=image"
              target="_blank"
              rel="noopener noreferrer"
              sx={milestoneExternalLinkSx}
            >
              Substack
            </Link>
            , which hosts the more unfiltered, and the not-so-polished reality that keeps everything moving.
          </>
        ),
        image: "https://storage.googleapis.com/cuo-website/comedy.jpg",
        imageAlt: "ComedyUO looking ahead",
      },
    ],
  },
];

function YearHeading({
  year,
  theme,
  isFirstYear,
}: {
  year: string;
  theme?: string;
  isFirstYear?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [revealed, setRevealed] = useState(!!isFirstYear);

  useEffect(() => {
    if (isFirstYear) return;
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setRevealed(true);
      },
      { root: null, rootMargin: "0px 0px -12% 0px", threshold: [0.08, 0.2] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [isFirstYear]);

  return (
    <Box
      ref={rootRef}
      data-timeline-year={year}
      sx={{ textAlign: "center", position: "relative", zIndex: 1 }}
    >
      <Typography
        component="h2"
        sx={{
          fontWeight: 800,
          fontSize: { xs: "4.25rem", sm: "6rem", md: "8rem" },
          lineHeight: 0.98,
          letterSpacing: "-0.045em",
          color: revealed ? "#ffffff" : "rgba(255, 255, 255, 0.22)",
          textShadow: revealed
            ? `0 2px 48px rgba(255, 255, 255, 0.14), 0 0 72px rgba(201, 168, 76, 0.18)`
            : "none",
          mb: theme ? { xs: 0.75, md: 1 } : { xs: 0.5, md: 0.75 },
          mt: isFirstYear ? { xs: 1.5, md: 2 } : { xs: 2.25, md: 3 },
          fontFamily: pageFont,
          transition: "color 0.7s ease, text-shadow 0.7s ease",
        }}
      >
        {year}
      </Typography>
      {theme && (
        <Typography
          sx={{
            fontFamily: pageFont,
            fontSize: { xs: "1.0625rem", sm: "1.25rem", md: "1.5rem" },
            fontWeight: 600,
            letterSpacing: { xs: "0.2em", md: "0.24em" },
            textTransform: "uppercase",
            color: revealed ? "rgba(230, 232, 238, 0.96)" : "rgba(230, 232, 238, 0.28)",
            mb: 0,
            transition: "color 0.7s ease",
          }}
        >
          {theme}
        </Typography>
      )}
    </Box>
  );
}

/** Collapsed body (4 lines) + read-more row — timeline gaps stay fixed when expanded */
const READMORE_COLLAPSED_BODY = { xs: "6.1rem", md: "6.35rem" };
const READMORE_BLOCK_MIN = {
  xs: "calc(6.1rem + 2.25rem)",
  md: "calc(6.35rem + 2.25rem)",
};

const readMoreToggleSx = {
  display: "block",
  p: 0,
  fontFamily: pageFont,
  fontSize: "0.8125rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#fff",
  opacity: 0.85,
  textDecoration: "underline",
  textUnderlineOffset: "3px",
  "&:hover": { opacity: 1 },
} as const;

function MilestoneBody({
  text,
  /** Extra space below expanded copy when this card sits just above the next year heading. */
  padExpandedAboveNextYear,
}: {
  text: ReactNode;
  padExpandedAboveNextYear?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);
  const textRef = useRef<HTMLParagraphElement | null>(null);

  useLayoutEffect(() => {
    if (expanded) return;
    const el = textRef.current;
    if (!el) return;

    const measure = () => {
      setCanToggle(el.scrollHeight > el.clientHeight + 2);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [text, expanded]);

  const bodyTypographySx = {
    fontFamily: pageFont,
    fontSize: { xs: "0.9rem", md: "0.9375rem" },
    lineHeight: 1.75,
    color: palette.muted,
  } as const;

  const reserveCollapsedFootprint = canToggle && !expanded;

  const expandedPanelOuterSx = {
    display: "flex",
    flexDirection: "column",
    maxHeight: "min(72vh, 540px)",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
    backgroundColor: "transparent",
  } as const;

  const expandedPanelScrollSx = {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    px: 0,
    pt: 0,
  } as const;

  const expandedReadLessRowSx = {
    flexShrink: 0,
    pt: 1.5,
    backgroundColor: "transparent",
  } as const;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        ...(reserveCollapsedFootprint && { minHeight: READMORE_BLOCK_MIN }),
        ...(padExpandedAboveNextYear && expanded && canToggle && { pb: { xs: 1.5, md: 2 } }),
      }}
    >
      {!expanded ? (
        <>
          <Typography
            ref={textRef}
            sx={{
              ...bodyTypographySx,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 4,
              overflow: "hidden",
              minHeight: canToggle ? READMORE_COLLAPSED_BODY : undefined,
            }}
          >
            {text}
          </Typography>
          {canToggle && (
            <ButtonBase
              onClick={() => setExpanded(true)}
              disableRipple
              sx={{ ...readMoreToggleSx, mt: 1 }}
            >
              Read more
            </ButtonBase>
          )}
        </>
      ) : (
        canToggle && (
          <Box sx={expandedPanelOuterSx}>
            <Box sx={expandedPanelScrollSx}>
              <Typography sx={bodyTypographySx}>{text}</Typography>
            </Box>
            <Box sx={expandedReadLessRowSx}>
              <ButtonBase
                onClick={() => setExpanded(false)}
                disableRipple
                sx={{ ...readMoreToggleSx, mt: 0 }}
              >
                Read less
              </ButtonBase>
            </Box>
          </Box>
        )
      )}
    </Box>
  );
}

function MilestoneCard({
  milestone,
  padExpandedAboveNextYear,
}: {
  milestone: Milestone;
  padExpandedAboveNextYear?: boolean;
}) {
  const overlap = { xs: "-18px", md: "-22px" };
  const textInset = { xs: 2, md: 2.5 };
  const containPct = milestone.imageContainPercent ?? 58;

  return (
    <Box
      sx={{
        width: { xs: "100%", md: "min(100%, 520px)" },
        maxWidth: { xs: "100%", md: 520 },
        flexShrink: 0,
        alignSelf: "flex-start",
        position: "relative",
        overflow: "visible",
        boxSizing: "border-box",
        /* room so the overlapping text box does not clip */
        pb: { xs: 1.25, md: 1.5 },
      }}
    >
      <Box
        sx={{
          borderRadius: "16px",
          overflow: "hidden",
          border: `1px solid ${palette.border}`,
          backgroundColor: milestone.imageFit === "contain" ? palette.imageWellContain : palette.bg,
          ...(milestone.imageFit === "contain" && {
            backgroundImage:
              "radial-gradient(ellipse 72% 68% at 50% 44%, rgba(45, 45, 52, 0.5) 0%, transparent 62%)",
            boxShadow: "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
          }),
          width: "100%",
          height: { xs: 280, sm: 320, md: 360 },
          minHeight: { xs: 280, sm: 320, md: 360 },
          maxHeight: { xs: 280, sm: 320, md: 360 },
          position: "relative",
          boxSizing: "border-box",
          flexShrink: 0,
        }}
      >
        {milestone.imageFit === "contain" ? (
          <Box
            component="img"
            src={milestone.image}
            alt={milestone.imageAlt}
            sx={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              maxWidth: `${containPct}%`,
              maxHeight: `${containPct}%`,
              width: "auto",
              height: "auto",
              objectFit: "contain",
              objectPosition: "center",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={milestone.image}
              alt={milestone.imageAlt}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          mt: overlap,
          mx: textInset,
          p: { xs: 2, md: 2.5 },
          minWidth: 0,
          maxWidth: "100%",
          boxSizing: "border-box",
          backgroundColor: palette.bubbleSurface,
          border: `1px solid ${palette.border}`,
          borderRadius: "12px",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.45)",
          overflow: "visible",
        }}
      >
        <Typography
          sx={{
            fontFamily: pageFont,
            fontWeight: 700,
            fontSize: { xs: "1.2rem", md: "1.35rem" },
            color: "#fff",
            lineHeight: 1.25,
            mb: milestone.subtitle ? 0.5 : 1,
          }}
        >
          {milestone.title}
        </Typography>
        {milestone.subtitle && (
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: "0.7rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: palette.muted,
              mb: 1.25,
            }}
          >
            {milestone.subtitle}
          </Typography>
        )}
        <MilestoneBody text={milestone.body} padExpandedAboveNextYear={padExpandedAboveNextYear} />
      </Box>
    </Box>
  );
}

type RowAlign = "left" | "right";

function TimelineMilestoneRow({
  milestone,
  align,
  isLastInYear,
  isFirstInYear,
  revealDelay = 0,
}: {
  milestone: Milestone;
  align: RowAlign;
  isLastInYear: boolean;
  isFirstInYear?: boolean;
  revealDelay?: number;
}) {
  const isLeft = align === "left";
  const cardRevealVariant = isLeft ? "fadeLeft" : "fadeRight";

  const connector = (
    <Box
      sx={{
        display: { xs: "none", md: "block" },
        position: "absolute",
        top: "50%",
        height: "1px",
        backgroundColor: palette.spine,
        transform: "translateY(-50%)",
        width: 48,
        ...(isLeft ? { right: -48 } : { left: -48 }),
      }}
    />
  );

  return (
    <Box
      sx={{
        mt: isFirstInYear ? { xs: 2.25, md: 3 } : 0,
        mb: isLastInYear ? { xs: 1.25, md: 2 } : { xs: 2.5, md: 3.5 },
        position: "relative",
        zIndex: 1,
      }}
    >
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <ScrollReveal variant="fadeUp" delay={revealDelay}>
          <MilestoneCard milestone={milestone} padExpandedAboveNextYear={isLastInYear} />
        </ScrollReveal>
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "row",
          alignItems: "stretch",
          justifyContent: "center",
          position: "relative",
        }}
      >
        {isLeft ? (
          <>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                pr: "28px",
                position: "relative",
              }}
            >
              <ScrollReveal
                variant={cardRevealVariant}
                delay={revealDelay}
                style={{ position: "relative", width: "min(100%, 520px)" }}
              >
                <MilestoneCard milestone={milestone} padExpandedAboveNextYear={isLastInYear} />
                {connector}
              </ScrollReveal>
            </Box>
            <Box
              sx={{
                width: 16,
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                zIndex: 2,
              }}
            >
              <ScrollReveal variant="fadeIn" delay={revealDelay + 0.12} duration={0.45}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    border: `2px solid ${palette.bg}`,
                    boxShadow: `0 0 0 1px rgba(201, 168, 76, 0.45)`,
                  }}
                />
              </ScrollReveal>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }} />
          </>
        ) : (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }} />
            <Box
              sx={{
                width: 16,
                flexShrink: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "relative",
                zIndex: 2,
              }}
            >
              <ScrollReveal variant="fadeIn" delay={revealDelay + 0.12} duration={0.45}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#fff",
                    border: `2px solid ${palette.bg}`,
                    boxShadow: `0 0 0 1px rgba(201, 168, 76, 0.45)`,
                  }}
                />
              </ScrollReveal>
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                pl: "28px",
                position: "relative",
              }}
            >
              <ScrollReveal
                variant={cardRevealVariant}
                delay={revealDelay}
                style={{ position: "relative", width: "min(100%, 520px)" }}
              >
                <MilestoneCard milestone={milestone} padExpandedAboveNextYear={isLastInYear} />
                {connector}
              </ScrollReveal>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function AboutSection() {
  const timelineScrollRef = useRef<HTMLDivElement | null>(null);
  const [timelineVignette, setTimelineVignette] = useState(false);

  useEffect(() => {
    const update = () => {
      const el = timelineScrollRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const intersects = rect.top < window.innerHeight && rect.bottom > 0;
      const moreBelow = rect.bottom > window.innerHeight + 40;
      setTimelineVignette(intersects && moreBelow);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  /** Keeps timeline column + image widths stable when body scrollbar appears (e.g. inline read more). */
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.scrollbarGutter;
    root.style.scrollbarGutter = "stable";
    return () => {
      root.style.scrollbarGutter = prev;
    };
  }, []);

  return (
    <>
      <AboutLayoutHelmet />
      {timelineVignette && (
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            height: { xs: 96, md: 120 },
            pointerEvents: "none",
            zIndex: 12,
            background:
              "linear-gradient(to top, rgba(0,0,0,0.96) 0%, rgba(0,0,0,0.5) 45%, rgba(0,0,0,0) 100%)",
          }}
        />
      )}
      <Header variant="dark" />
      <Box component="main" sx={{ overflowX: "hidden", fontFamily: pageFont }}>
        <Box
          sx={{
            position: "relative",
            backgroundImage:
              "url('https://storage.googleapis.com/cuo-website/best+comedy+nyc+tonight.jpeg')",
            backgroundSize: "cover",
            backgroundPosition: "center 42%",
            minHeight: { xs: "min-content", md: 220 },
            py: { xs: 3, md: 3.5 },
            display: "flex",
            alignItems: "center",
            fontFamily: pageFont,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.74)",
              zIndex: 0,
            }}
          />
          <Container
            maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH}
            disableGutters
            sx={{ ...publicPageContainerSx, position: "relative", zIndex: 1 }}
          >
            <Typography
              component="span"
              sx={{
                display: "block",
                fontFamily: pageFont,
                fontSize: "0.75rem",
                fontWeight: 500,
                letterSpacing: { xs: "0.08em", sm: "0.12em" },
                color: "#fff",
                textTransform: "uppercase",
                mb: 1,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              About Us
            </Typography>
            <Typography
              variant="h1"
              sx={{
                fontFamily: pageFont,
                fontWeight: 700,
                fontSize: { xs: "2.25rem", sm: "3.25rem", md: "4rem" },
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                color: "#fff",
                whiteSpace: { xs: "normal", md: "nowrap" },
                wordBreak: "break-word",
              }}
            >
              Figuring It Out As We Go
            </Typography>
          </Container>
        </Box>

        <Box
          sx={{
            backgroundColor: "#1a1a1e",
            py: { xs: 5, md: 6 },
            fontFamily: pageFont,
          }}
        >
          <Container
            maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH}
            disableGutters
            sx={publicPageContainerSx}
          >
            <Grid container spacing={3} alignItems="stretch">
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <ScrollReveal variant="fadeUp" style={{ display: "flex", width: "100%" }}>
                  <AboutCard
                    imageSrc="https://storage.googleapis.com/cuo-website/Wilson+Show.jpg"
                    title="90+ Unique Venues"
                    subtitle="And counting..."
                    description="David and Ethan founded ComedyUO in April 2021. What began as a secret gathering for family and friends soon grew into the live event company that's now shaking up New York City's stand up comedy scene. You may have seen us walking around the city. We're the curly-headed guys producing shows in the laundromat under your apartment and getting shooed out of snooty shops in Soho."
                  />
                </ScrollReveal>
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <ScrollReveal variant="fadeUp" delay={0.12} style={{ display: "flex", width: "100%" }}>
                  <AboutCard
                    imageSrc="https://storage.googleapis.com/cuo-website/Desert+5.jpg"
                    title="350+ Shows"
                    description="Underground Overground Comedy produces pop-up comedy shows in some of New York City's most iconic businesses, from Katz's Delicatessen to Economy Candy. Tickets to our events are all-inclusive, so you never have to worry about hitting a drink minimum or pulling out your wallet. Lineups are kept secret until the comics come to the stage, so you never know who will show up!"
                  />
                </ScrollReveal>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>

      {/* Our Story + timeline */}
      <Box
        ref={timelineScrollRef}
        sx={{
          backgroundColor: "#000000",
          overflowX: "hidden",
          fontFamily: pageFont,
        }}
      >
        <Box
          sx={{
            backgroundColor: "#000000",
            pt: { xs: 4, md: 6 },
            pb: { xs: 1.5, md: 2 },
          }}
        >
          <Container
            maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH}
            disableGutters
            sx={publicPageContainerSx}
          >
            <Typography
              component="p"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.2em",
                color: palette.muted,
                textTransform: "uppercase",
                textAlign: "center",
                mb: 2,
              }}
            >
              Our Story
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: {
                  xs: "1.55rem",
                  sm: "clamp(1.25rem, 3.6vw, 2.35rem)",
                  md: "clamp(1.65rem, 2.35vw, 2.95rem)",
                  lg: "clamp(2rem, 2.5vw, 3.35rem)",
                },
                lineHeight: 1.12,
                letterSpacing: "-0.03em",
                color: "#fff",
                textAlign: "center",
                maxWidth: "min(100%, 1100px)",
                mx: "auto",
                mb: 2.5,
                whiteSpace: { xs: "normal", sm: "nowrap" },
              }}
            >
              From a small gym to The Empire State Building
            </Typography>
            <Typography
              sx={{
                color: palette.muted,
                fontSize: {
                  xs: "0.9rem",
                  sm: "clamp(0.78rem, 1.9vw, 1.0625rem)",
                  md: "clamp(0.88rem, 1.35vw, 1.0625rem)",
                },
                lineHeight: 1.45,
                textAlign: "center",
                maxWidth: "min(100%, 960px)",
                mx: "auto",
                whiteSpace: { xs: "normal", sm: "nowrap" },
              }}
            >
              The biggest shows, the unforgettable nights, and the milestones that built ComedyUO.
            </Typography>
          </Container>
        </Box>

        <Box
          sx={{
            position: "relative",
            pb: { xs: 5, md: 8 },
            backgroundColor: "#000000",
          }}
        >
          <Container
            maxWidth={PUBLIC_PAGE_CONTAINER_MAX_WIDTH}
            disableGutters
            sx={{ ...publicPageContainerSx, position: "relative" }}
          >
            {TIMELINE.map((block, yearIndex) => (
              <Box key={block.year}>
                <YearHeading year={block.year} theme={block.theme} isFirstYear={yearIndex === 0} />
                <Box sx={{ position: "relative" }}>
                  <Box
                    aria-hidden
                    sx={{
                      display: { xs: "none", md: "block" },
                      position: "absolute",
                      left: "50%",
                      transform: "translateX(-50%)",
                      top: 0,
                      bottom: 0,
                      width: "1px",
                      backgroundColor: palette.spine,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  {block.milestones.map((m, mi) => {
                    const rowIndex =
                      TIMELINE.slice(0, yearIndex).reduce((acc, y) => acc + y.milestones.length, 0) +
                      mi;
                    const align: RowAlign = rowIndex % 2 === 0 ? "left" : "right";
                    const isLast = mi === block.milestones.length - 1;
                    return (
                      <TimelineMilestoneRow
                        key={`${block.year}-${m.title}-${mi}`}
                        milestone={m}
                        align={align}
                        isLastInYear={isLast}
                        isFirstInYear={mi === 0}
                        revealDelay={Math.min(rowIndex * 0.05, 0.35)}
                      />
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Container>
        </Box>
      </Box>

      <Footer variant="dark" />
    </>
  );
}
